import { cn } from '@shared/lib/utils'
import { getRiskLevel, type RiskLevel } from '@shared/utils/risk'

interface RiskBadgeProps {
  score: number
  size?: 'sm' | 'default'
  className?: string
}

const LEVEL_CLASSES: Record<RiskLevel, { text: string; bg: string; border: string }> = {
  low:      { text: 'text-status-success-text', bg: 'bg-status-success-bg', border: 'border-status-success-border' },
  medium:   { text: 'text-status-warning-text', bg: 'bg-status-warning-bg', border: 'border-status-warning-border' },
  high:     { text: 'text-status-error-text',   bg: 'bg-status-error-bg',   border: 'border-status-error-border' },
  critical: { text: 'text-status-error-text',   bg: 'bg-status-error-bg',   border: 'border-status-error-border' },
}

const LEVEL_LABELS: Record<RiskLevel, string> = {
  low: 'Low Risk',
  medium: 'Medium Risk',
  high: 'High Risk',
  critical: 'Critical Risk',
}

/**
 * Shared risk score badge — color-coded by risk level.
 *
 * Sizes:
 *  - "default" — fixed square with border (tables/lists)
 *  - "sm"      — compact padding-based label (metadata/inline)
 */
export function RiskBadge({ score, size = 'default', className }: RiskBadgeProps) {
  const level = getRiskLevel(score)
  const colors = LEVEL_CLASSES[level]

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-sm font-medium',
        colors.text,
        colors.bg,
        size === 'sm'
          ? 'px-1.5 py-0.5 text-[10px]'
          : 'min-w-5 h-5 text-xs border ' + colors.border,
        className,
      )}
      title={LEVEL_LABELS[level]}
    >
      {score}
    </span>
  )
}
